import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  Row,
  Spin,
  Typography,
} from "antd";
import { LockOutlined, MailOutlined } from "@ant-design/icons";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import API_CONFIG from "../config/api.js";
import { ELSA_BRAND_ASSETS } from "../config/brandAssets";
import { useAuth } from "../context/AuthContext";
import ElsaSplashVideo from "../components/ElsaSplashVideo";
import SecureLogger from "../utils/secureLogger";
import "./Login.css";

const { Title, Text } = Typography;

const platformHighlights = [
  {
    label: "Gestion commerciale",
    value: "Ventes, stocks et achats",
    detail: "Caisse, catalogue, réassort et facturation",
  },
  {
    label: "Logistique",
    value: "Livraisons et opérations",
    detail: "Transports, expéditions et flux",
  },
  {
    label: "Hôtellerie",
    value: "Hébergement et service",
    detail: "Réservations, chambres et suivi",
  },
  {
    label: "Restauration",
    value: "Cuisine et service en salle",
    detail: "Commandes, menus et encaissement",
  },
  {
    label: "Production industrielle",
    value: "Ordres et suivi de production",
    detail: "Unités, coûts et transformation",
  },
  {
    label: "Ressources humaines",
    value: "Paie et administration",
    detail: "Équipes, postes et conformité",
  },
  {
    label: "Pilotage",
    value: "Multi-sites et reporting",
    detail: "Entreprises, établissements et indicateurs",
  },
];

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState(null);
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form] = Form.useForm();
  const requestedNext = new URLSearchParams(window.location.search).get("next");
  const safeNext =
    requestedNext && requestedNext.startsWith("/payment") && !requestedNext.startsWith("//")
      ? requestedNext
      : null;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("session") === "expired") {
      setSessionExpiredMessage("Votre session a expiré. Veuillez vous reconnecter.");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const getErrorMessage = (errorResponse) => {
    const errorData = errorResponse?.data;

    if (!errorData) {
      return "Échec de la connexion. Vérifiez vos identifiants.";
    }

    switch (errorData.code) {
      case "COMPANY_INACTIVE":
        return "Votre entreprise est inactive. Veuillez contacter le support pour réactiver votre compte.";
      case "NO_WAREHOUSE_ACCESS":
        return "Aucun établissement n'est associé à votre compte. Veuillez contacter l'administrateur pour obtenir les accès nécessaires.";
      case "ALL_WAREHOUSES_INACTIVE": {
        let message = "Tous vos établissements ou leurs entreprises sont inactifs. ";
        if (errorData.details && Array.isArray(errorData.details)) {
          message += `Détails : ${errorData.details.join(", ")}`;
        }
        message += " Veuillez contacter l'administrateur.";
        return message;
      }
      case "SINGLE_WAREHOUSE_INACTIVE":
        return (
          errorData.message ||
          "L'établissement associé à votre compte est inactif. Veuillez contacter l'administrateur."
        );
      default:
        return (
          errorData.message ||
          errorData.error ||
          "Échec de la connexion. Vérifiez vos identifiants."
        );
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    setError(null);
    SecureLogger.debug("Login attempt started");

    try {
      const response = await axios.post(API_CONFIG.apiUrl("/login"), {
        email: values.email,
        password: values.password,
      });

      SecureLogger.info("Login successful");

      if (response.data.token && response.data.user) {
        login(response.data.token, response.data.user);

        if (response.data.user.hasLimitedAccess) {
          SecureLogger.warn("User has limited access to warehouses");
        }

        navigate(
          safeNext || (response.data.user.subscription_portal_only
            ? "/souscription"
            : "/accueil")
        );
      } else {
        SecureLogger.error("Login response missing token or user data");
        setError("Réponse invalide du serveur après la connexion.");
      }
    } catch (err) {
      SecureLogger.authError("Login", err);

      setError(getErrorMessage(err.response));
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    SecureLogger.debug("Form validation failed", {
      fieldCount: errorInfo.errorFields?.length,
    });
  };

  return (
    <div className="login-shell">
      <div className="login-shell__ambient login-shell__ambient--left" aria-hidden="true" />
      <div className="login-shell__ambient login-shell__ambient--right" aria-hidden="true" />

      <div className="login-shell__grid">
        <section className="login-brand-panel" aria-label="Présentation Elsa Gestion">
          <img
            className="login-brand-panel__logo"
            src={ELSA_BRAND_ASSETS.logoHorizontalWhite}
            alt="ELSA GESTION — Gardez le contrôle, où que vous soyez."
          />
          <div className="login-brand-panel__pill">Accès sécurisé</div>
          <div className="login-brand-panel__content">
            <p className="login-brand-panel__eyebrow">ELSA GESTION</p>
            <Title level={1} className="login-brand-panel__title">
              Retrouvez toute votre gestion dans un seul espace.
            </Title>
            <Text className="login-brand-panel__description">
              Connectez-vous pour retrouver vos ventes, vos stocks, vos achats,
              votre logistique, votre hôtellerie, votre restauration, votre
              production industrielle, vos équipes et vos indicateurs dans un
              espace de travail stable, rapide et cohérent.
            </Text>
          </div>
          <div className="login-brand-panel__highlights">
            {platformHighlights.map((highlight) => (
              <div key={highlight.label} className="login-brand-panel__highlight">
                <span className="login-brand-panel__highlightLabel">{highlight.label}</span>
                <strong className="login-brand-panel__highlightValue">{highlight.value}</strong>
                <span className="login-brand-panel__highlightMeta">{highlight.detail}</span>
              </div>
            ))}
          </div>
        </section>

        <Card className="login-card">
          <Spin spinning={loading} tip="Connexion...">
            <div className="login-card__brand">
              <div className="login-card__logo-frame">
                <ElsaSplashVideo
                  className="login-card__logo-video"
                  fallbackClassName="login-card__logo"
                  fallbackSrc={ELSA_BRAND_ASSETS.emblemColor}
                />
              </div>
              <div className="login-card__copy">
                <span className="login-card__eyebrow">Connexion</span>
                <Title level={3} className="login-card__title">
                  Connexion à votre espace
                </Title>
                <Text className="login-card__subtitle">
                  Utilisez vos identifiants pour accéder à votre environnement de travail.
                </Text>
              </div>
            </div>

            <Form
              form={form}
              name="login"
              initialValues={{ remember: true }}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
              layout="vertical"
              requiredMark={false}
              className="login-form"
            >
              {sessionExpiredMessage && (
                <Form.Item className="login-form__alert">
                  <Alert
                    message={sessionExpiredMessage}
                    type="warning"
                    showIcon
                    closable
                    onClose={() => setSessionExpiredMessage(null)}
                  />
                </Form.Item>
              )}

              {error && (
                <Form.Item className="login-form__alert">
                  <Alert
                    message={error}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setError(null)}
                    style={{
                      wordWrap: "break-word",
                      whiteSpace: "pre-wrap",
                    }}
                  />
                </Form.Item>
              )}

              <Form.Item
                name="email"
                rules={[
                  { required: true, message: "Veuillez entrer votre email!" },
                  { type: "email", message: "Veuillez entrer un email valide!" },
                ]}
                className="login-form__item"
              >
                <Input
                  prefix={<MailOutlined className="login-form__icon" />}
                  placeholder="Email"
                  size="large"
                  className="login-form__input"
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  {
                    required: true,
                    message: "Veuillez entrer votre mot de passe!",
                  },
                ]}
                className="login-form__item login-form__item--password"
              >
                <Input.Password
                  prefix={<LockOutlined className="login-form__icon" />}
                  placeholder="Mot de passe"
                  size="large"
                  className="login-form__input"
                />
              </Form.Item>

              <Form.Item className="login-form__meta">
                <Row justify="space-between" align="middle" gutter={[0, 12]}>
                  <Col xs={{ span: 24, order: 1 }} sm={{ span: 12, order: 1 }}>
                    <Form.Item name="remember" valuePropName="checked" noStyle>
                      <Checkbox className="login-form__checkbox">Se souvenir de moi</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col xs={{ span: 24, order: 2 }} sm={{ span: 12, order: 2 }}>
                    <Link to="/forgot-password" className="login-form__link">
                      Mot de passe oublié ?
                    </Link>
                  </Col>
                </Row>
              </Form.Item>

              <Form.Item className="login-form__submit">
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  loading={loading}
                  className="login-form__button"
                >
                  Se Connecter
                </Button>
              </Form.Item>
            </Form>

            <div className="login-card__footer">
              <Text className="login-card__footerText">
                Pas de compte ?{" "}
                <Link to="/signup" className="login-card__footerLink">
                  S'inscrire
                </Link>
              </Text>
            </div>
          </Spin>
        </Card>
      </div>

    </div>
  );
};

export default Login;
